import React from 'react';
import { StyleSheet, TouchableOpacity } from 'react-native';

import { Text } from '@/components/TextComponent';
import { Box, Image } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { GlowShape } from '@/feature/promohub/components/OvalGlow';
import { common } from '@/styles/styles';
import { toLocaleCurrency } from '@/utils/numeric/currency';

import { RipsPack, RipsPackStatus, RipsPackTier } from '../hooks/use-rips-games';

export const RIPS_PACK_CARD_RADIUS = 24;
export const RIPS_PACK_TILE_ASPECT_RATIO = 173 / 211;
const PACK_IMAGE_ASPECT_RATIO = 93 / 158;

// Blurred oval behind the pack, tilted 30° (Figma: 37×131 ellipse, blur 30), with room around it for the blur to fade out.
const GLOW_WIDTH = 37;
const GLOW_HEIGHT = 131;
const GLOW_BLUR = 30;
const GLOW_OPACITY = 0.9;
const GLOW_INSET = 90;

const PACK_GLOW_COLORS: Record<RipsPackTier, string> = {
    [RipsPackTier.Starter]: '#EDEDED',
    [RipsPackTier.Premier]: '#B175F7',
    [RipsPackTier.Bronze]: '#9D806C',
    [RipsPackTier.Silver]: '#8C9096',
    [RipsPackTier.Gold]: '#DABE8C',
    [RipsPackTier.Sapphire]: '#1D388D',
    [RipsPackTier.Emerald]: '#4BA47A',
    [RipsPackTier.Ruby]: '#CD3C41',
    [RipsPackTier.Diamond]: '#4294B5',
};

type RipsPackCardProps = {
    pack: RipsPack;
    width: number;
    onPress: (pack: RipsPack) => void;
};

/**
 * Pack card of the Rips lobby: tile with the pack artwork centered over its tier glow, then name, max pull and price.
 */
export const RipsPackCard = ({ pack, width, onPress }: RipsPackCardProps) => {
    // default to Starter tier glow if no badge is present
    const glowColor = pack.badge
        ? PACK_GLOW_COLORS[pack.badge.toLowerCase() as RipsPackTier]
        : PACK_GLOW_COLORS[RipsPackTier.Starter];

    const isSoldOut = !!pack.status && pack.status.toLowerCase() === RipsPackStatus.Restocking;
    const showGlow = !isSoldOut && !!glowColor;

    return (
        <TouchableOpacity
            onPress={() => onPress(pack)}
            activeOpacity={0.8}
            disabled={isSoldOut}
            style={[styles.card, { width }]}
            testID={`ripsPackCard-${pack.vendorId}`}
        >
            <Box style={styles.tile}>
                {showGlow ? (
                    <Box style={styles.glow}>
                        <GlowShape
                            x={GLOW_INSET}
                            y={GLOW_INSET}
                            width={GLOW_WIDTH}
                            height={GLOW_HEIGHT}
                            color={glowColor}
                            opacity={GLOW_OPACITY}
                            blur={GLOW_BLUR}
                            filterPadding={GLOW_INSET}
                        />
                    </Box>
                ) : null}
                <Image
                    source={{ uri: pack.thumbnailUrl }}
                    style={[styles.image, isSoldOut && styles.disabledImage]}
                    resizeMode="cover"
                />
            </Box>
            <Box style={styles.texts}>
                <Text
                    variant="labelLarge2"
                    color={isSoldOut ? ds.colorToken.text.disabled : ds.colorToken.text.primary}
                    numberOfLines={1}
                >
                    {pack.name}
                </Text>
                <Text
                    variant="bodySmall2"
                    color={isSoldOut ? ds.colorToken.text.disabled : ds.colorToken.text.secondary}
                    numberOfLines={1}
                >
                    {`Max pull: ${toLocaleCurrency(pack.maxValue)}`}
                </Text>
                <Text
                    variant="labelLarge2"
                    color={isSoldOut ? ds.colorToken.text.disabled : ds.colorToken.text.primary}
                    numberOfLines={1}
                >
                    {toLocaleCurrency(pack.priceCents / 100, false)}
                </Text>
            </Box>
            {isSoldOut ? (
                <Box
                    py={'s4'}
                    px={'s6'}
                    borderRadius={'r7'}
                    position={'absolute'}
                    style={common.borderCurve}
                    top={16}
                    left={16}
                    backgroundColor={ds.colorToken.background.neutral.inverse}
                >
                    <Text variant="labelMedium" color={ds.colorToken.text.inverse}>
                        Sold out
                    </Text>
                </Box>
            ) : null}
        </TouchableOpacity>
    );
};

const styles = StyleSheet.create({
    card: {
        gap: 8,
    },
    tile: {
        aspectRatio: RIPS_PACK_TILE_ASPECT_RATIO,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: RIPS_PACK_CARD_RADIUS,
        borderCurve: 'continuous',
        borderWidth: 1,
        borderColor: ds.colorValue.border.subtle,
        backgroundColor: ds.colorValue.background.neutral.subtler,
        overflow: 'hidden',
    },
    glow: {
        position: 'absolute',
        width: GLOW_WIDTH + 2 * GLOW_INSET,
        height: GLOW_HEIGHT + 2 * GLOW_INSET,
        transform: [{ rotate: '-30deg' }],
    },
    image: {
        height: '75%',
        aspectRatio: PACK_IMAGE_ASPECT_RATIO,
    },
    disabledImage: {
        opacity: 0.4,
    },
    texts: {
        paddingLeft: 4,
    },
});
